import React, { useState, useEffect } from 'react';
import '../styles/ExtractionValidator.css';
import { divisionToTrade } from '../services/simple-pdf-reader';

// UNIFIED: Import consistent trade map
const TRADE_MAP = {
  '00': 'General', '01': 'General', '02': 'Demolition', '03': 'Concrete',
  '04': 'Masonry', '05': 'Framing', '06': 'Framing', '07': 'Insulation',
  '08': 'Doors/Hardware', '09': 'Finishes', '10': 'General', '11': 'General',
  '12': 'General', '13': 'General', '14': 'General', '15': 'HVAC',
  '21': 'Fire Suppression', '22': 'Plumbing', '23': 'HVAC', '26': 'Electrical',
  '27': 'Electrical', '28': 'Electrical', '29': 'General', '30': 'Site',
  '31': 'Site', '32': 'Site', '33': 'Site', '34': 'Site', '35': 'Site'
};

function ExtractionValidator({ extractedData, onConfirm, onBack }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [newItemForm, setNewItemForm] = useState(false);
  const [newItem, setNewItem] = useState({
    divisionCode: '09',
    description: '',
    itemType: '',
    estimatedCost: '',
    quantities: []
  });

  // Load from extracted data first (fresh from server), fallback to localStorage
  useEffect(() => {
    if (extractedData?.items?.length > 0) {
      console.log('📂 Using fresh extracted data with', extractedData.items.length, 'items');
      
      // Ensure all items have confidence scores
      const itemsWithConfidence = extractedData.items.map(item => ({
        ...item,
        confidence: item.confidence !== undefined ? item.confidence : 0.75
      }));
      
      setItems(itemsWithConfidence);
      // SELECT ALL BY DEFAULT - FORCE ALL ITEMS SELECTED
      const allIds = new Set(itemsWithConfidence.map(i => i.id));
      setSelectedItems(allIds);
      console.log('✅ AUTO-SELECTED ALL', allIds.size, 'items (forcing 100% selection)');
      console.log('🎯 Selected item IDs:', Array.from(allIds).slice(0, 10), '...');
    } else {
      const saved = localStorage.getItem('extractorState');
      if (saved) {
        try {
          const state = JSON.parse(saved);
          console.log('📂 Loaded state from localStorage');
          const loadedItems = state.items || [];
          setItems(loadedItems);
          // FORCE SELECT ALL ITEMS FROM SAVED STATE
          const allIds = new Set(loadedItems.map(i => i.id));
          setSelectedItems(allIds);
          console.log('✅ Restored', allIds.size, 'items from localStorage (all selected)');
        } catch (error) {
          console.error('Failed to load localStorage:', error);
        }
      }
    }
  }, [extractedData]);

  // Save to localStorage whenever items or selection changes
  // ALWAYS SAVE ALL ITEMS AS SELECTED
  useEffect(() => {
    const allIds = items.map(i => i.id);
    localStorage.setItem('extractorState', JSON.stringify({
      items,
      selectedItems: allIds  // Always save all IDs as selected
    }));
  }, [items]);

  // Calculate statistics
  const stats = {
    totalItems: items.length,
    highConfidence: items.filter(i => i.confidence > 0.9).length,
    mediumConfidence: items.filter(i => i.confidence >= 0.7 && i.confidence <= 0.9).length,
    lowConfidence: items.filter(i => i.confidence < 0.7).length,
    selectedItems: items.length  // ALWAYS REPORT ALL ITEMS SELECTED
  };

  // Get items grouped by division - IMPROVED: Filter empty divisions
  const itemsByDivision = {};
  items.forEach(item => {
    if (!itemsByDivision[item.divisionCode]) {
      itemsByDivision[item.divisionCode] = [];
    }
    itemsByDivision[item.divisionCode].push(item);
  });

  // Start editing item
  const handleEditStart = (item) => {
    setEditingId(item.id);
    // FIXED: Deep copy all fields, not just some
    setEditValues({ ...item });
  };

  // Save edited item - FIXED: Proper merge, preserve all fields
  const handleEditSave = (id) => {
    const updatedItems = items.map(item => {
      if (item.id === id) {
        // Merge: take editValues as base, keep original fields that weren't edited
        return {
          ...item,
          ...editValues
        };
      }
      return item;
    });
    setItems(updatedItems);
    setEditingId(null);
    console.log('✅ Item saved with all fields preserved');
  };

  // Remove item
  const handleRemoveItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    selectedItems.delete(id);
    setSelectedItems(new Set(selectedItems));
    console.log('🗑️ Item removed');
  };

  // Toggle item selection
  const handleToggleItem = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  // Add new item - FIXED: Allow cost input, not hardcoded
  const handleAddItem = () => {
    // Validate required fields
    if (!newItem.description.trim()) {
      alert('Please enter a description');
      return;
    }

    if (!newItem.estimatedCost || isNaN(parseFloat(newItem.estimatedCost))) {
      alert('Please enter a valid cost');
      return;
    }

    const id = `item-new-${Date.now()}`;
    const itemToAdd = {
      id,
      divisionCode: newItem.divisionCode,
      divisionName: `Division ${newItem.divisionCode}`,
      trade: TRADE_MAP[newItem.divisionCode] || 'General',
      description: newItem.description.trim(),
      itemType: newItem.itemType || 'General',
      confidence: 0.85,
      keywords: [],
      quantities: newItem.quantities || [],
      estimatedCost: parseFloat(newItem.estimatedCost),
      quantity: 1,
      unit: 'Lot'
    };

    setItems([...items, itemToAdd]);
    console.log('✅ New item added with cost:', newItem.estimatedCost);

    // Reset form
    setNewItem({
      divisionCode: '09',
      description: '',
      itemType: '',
      estimatedCost: '',
      quantities: []
    });
    setNewItemForm(false);
    selectedItems.add(id);
    setSelectedItems(new Set(selectedItems));
  };

  // Confirm and generate estimate
  const handleConfirm = () => {
    // FORCE: Use ALL items, not just selected ones
    const finalItems = items;
    
    // Clear localStorage on confirm
    localStorage.removeItem('extractorState');
    
    onConfirm({
      items: finalItems,
      statistics: stats,
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className="extraction-validator">
      <div className="validator-header">
        <button className="btn-back" onClick={onBack}>← Back</button>
        <h2>📋 Validate Extracted Items</h2>
        <div className="header-stats">
          <span className="stat high">✅ {stats.highConfidence} High</span>
          <span className="stat medium">⚠️ {stats.mediumConfidence} Medium</span>
          <span className="stat low">❌ {stats.lowConfidence} Low</span>
          <span className="stat selected">✓ {items.length} Selected (All)</span>
        </div>
      </div>

      <div className="validator-body">
        {/* Statistics Panel */}
        <div className="stats-panel">
          <h3>Extraction Summary</h3>
          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-value">{extractedData.statistics.totalItems}</div>
              <div className="stat-label">Items Extracted</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">{extractedData.statistics.divisionsFound}</div>
              <div className="stat-label">CSI Divisions</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">{extractedData.statistics.tradesIdentified}</div>
              <div className="stat-label">Trades Identified</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">100%</div>
              <div className="stat-label">Selected for Estimate</div>
            </div>
          </div>
        </div>

        {/* Items by Division */}
        <div className="items-container">
          {Object.entries(itemsByDivision).length === 0 ? (
            <div className="empty-state">
              <p>No items found. Try uploading a different PDF or add items manually.</p>
            </div>
          ) : (
            Object.entries(itemsByDivision).map(([divCode, divItems]) => (
              <div key={divCode} className="division-group">
                <div className="division-header">
                  <h3>Division {divCode}</h3>
                  <span className="item-count">{divItems.length} items</span>
                </div>

                {divItems.map(item => (
                  <div key={item.id} className="item-row">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleToggleItem(item.id)}
                      className="item-checkbox"
                    />

                    {editingId === item.id ? (
                      // Edit mode - FIXED: All fields editable, preserve on save
                      <div className="item-edit">
                        <div className="edit-group">
                          <label>Description</label>
                          <textarea
                            value={editValues.description || ''}
                            onChange={(e) =>
                              setEditValues({ ...editValues, description: e.target.value })
                            }
                            className="edit-input"
                          />
                        </div>

                        <div className="edit-group">
                          <label>Type</label>
                          <input
                            type="text"
                            value={editValues.itemType || ''}
                            onChange={(e) =>
                              setEditValues({ ...editValues, itemType: e.target.value })
                            }
                            className="edit-input"
                          />
                        </div>

                        <div className="edit-group">
                          <label>Cost</label>
                          <input
                            type="number"
                            value={editValues.estimatedCost || ''}
                            onChange={(e) =>
                              setEditValues({ ...editValues, estimatedCost: parseFloat(e.target.value) || 0 })
                            }
                            className="edit-input"
                            step="0.01"
                          />
                        </div>

                        <div className="edit-controls">
                          <button
                            className="btn-save"
                            onClick={() => handleEditSave(item.id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display mode
                      <div className="item-content">
                        <div className="item-description">
                          <p>{item.description}</p>
                          {item.itemType && (
                            <span className="item-type">{item.itemType}</span>
                          )}
                        </div>

                        <div className="item-details">
                          {item.quantities && item.quantities.length > 0 && (
                            <div className="quantities">
                              {item.quantities.map((q, idx) => (
                                <span key={idx} className="quantity">
                                  {q.value} {q.type}
                                </span>
                              ))}
                            </div>
                          )}
                          {item.keywords && item.keywords.length > 0 && (
                            <div className="keywords">
                              {item.keywords.map((k, idx) => (
                                <span key={idx} className="keyword">{k}</span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="item-cost">
                          {item.materialCost ? (
                            <>
                              <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '4px' }}>
                                Mat: ${parseFloat(item.materialCost).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </div>
                              <div style={{ fontSize: '0.85em', color: '#666', marginBottom: '4px' }}>
                                Labor: ${parseFloat(item.laborCost || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </div>
                            </>
                          ) : null}
                          <strong>${parseFloat(item.cost || item.estimatedCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </div>

                        <div className="confidence-score">
                          <div className="confidence-bar">
                            <div
                              className="confidence-fill"
                              style={{
                                width: `${item.confidence * 100}%`,
                                backgroundColor:
                                  item.confidence > 0.9
                                    ? '#10b981'
                                    : item.confidence > 0.7
                                    ? '#f59e0b'
                                    : '#ef4444'
                              }}
                            />
                          </div>
                          <span className="confidence-label">
                            {item.confidence !== undefined && item.confidence !== null
                              ? (item.confidence * 100).toFixed(0)
                              : '75'}%
                          </span>
                        </div>

                        <div className="item-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEditStart(item)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Add New Item Form - FIXED: Cost input field added */}
        {newItemForm ? (
          <div className="new-item-form">
            <h3>Add New Item</h3>
            <div className="form-group">
              <label>Division</label>
              <select
                value={newItem.divisionCode}
                onChange={(e) =>
                  setNewItem({ ...newItem, divisionCode: e.target.value })
                }
              >
                <option value="00">Division 00 - Procurement</option>
                <option value="01">Division 01 - General Requirements</option>
                <option value="02">Division 02 - Existing Conditions</option>
                <option value="03">Division 03 - Concrete</option>
                <option value="05">Division 05 - Metals</option>
                <option value="06">Division 06 - Wood & Composites</option>
                <option value="07">Division 07 - Thermal & Moisture</option>
                <option value="08">Division 08 - Openings</option>
                <option value="09">Division 09 - Finishes</option>
                <option value="21">Division 21 - Fire Suppression</option>
                <option value="22">Division 22 - Plumbing</option>
                <option value="23">Division 23 - HVAC</option>
                <option value="26">Division 26 - Electrical</option>
              </select>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={newItem.description}
                onChange={(e) =>
                  setNewItem({ ...newItem, description: e.target.value })
                }
                placeholder="Describe the item..."
              />
            </div>

            <div className="form-group">
              <label>Item Type</label>
              <input
                type="text"
                value={newItem.itemType}
                onChange={(e) =>
                  setNewItem({ ...newItem, itemType: e.target.value })
                }
                placeholder="e.g., HVAC Equipment, Finishes, etc"
              />
            </div>

            <div className="form-group">
              <label>Estimated Cost * ($)</label>
              <input
                type="number"
                value={newItem.estimatedCost}
                onChange={(e) =>
                  setNewItem({ ...newItem, estimatedCost: e.target.value })
                }
                placeholder="e.g., 1500"
                step="0.01"
                min="0"
              />
            </div>

            <div className="form-actions">
              <button className="btn-add" onClick={handleAddItem}>
                Add Item
              </button>
              <button
                className="btn-cancel"
                onClick={() => setNewItemForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            className="btn-add-item"
            onClick={() => setNewItemForm(true)}
          >
            + Add Missing Item
          </button>
        )}
      </div>

      {/* Action Footer */}
      <div className="validator-footer">
        <div className="footer-info">
          <p>
            📊 {stats.selectedItems} of {stats.totalItems} items selected
          </p>
          <p className="footer-hint">
            Review extraction. Edit items with ⚠️ low confidence. Add any missing scope.
          </p>
        </div>
        <div className="footer-actions">
          <button className="btn-cancel" onClick={onBack}>
            Back
          </button>
          <button
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={items.length === 0}
          >
            Generate Estimate ({items.length} items)
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExtractionValidator;
